import { type FormEvent, type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { getFirebase } from "../firebase/init.js";
import { applyWriteMap } from "../firebase/writes.js";
import {
  type CustomExtra,
  type CustomizationRecord,
  addLibraryExtra,
  removeLibraryExtra,
} from "../lib/customization-edits.js";

const KINDS: CustomExtra["kind"][] = ["companion", "gear", "vehicle", "power", "other"];

interface Props {
  tablePath: string;
  customization: CustomizationRecord;
}

function slug(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48);
}

export default function CustomizationExtrasEditor({
  tablePath,
  customization,
}: Props): ReactElement {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [kind, setKind] = useState<CustomExtra["kind"]>("gear");

  async function write(writes: Record<string, unknown>): Promise<void> {
    const { db } = getFirebase();
    await applyWriteMap(db, writes);
  }

  async function handleAdd(e: FormEvent): Promise<void> {
    e.preventDefault();
    const id = slug(name);
    if (!id) return;
    await write(addLibraryExtra(tablePath, customization, { id, name: name.trim(), kind }));
    setName("");
    setKind("gear");
  }

  const extras = customization.extras.addedLibrary ?? [];

  return (
    <section className="space-y-3">
      <h3 className="text-sm font-semibold uppercase text-muted-foreground">
        {t("customization.extras.library")}
      </h3>
      <ul className="space-y-1 text-sm">
        {extras.map((e) => (
          <li key={e.id} className="flex items-center gap-2">
            <span className="flex-1">
              <span className="font-medium">{e.name}</span>{" "}
              <span className="text-xs text-muted-foreground">· {e.kind}</span>
            </span>
            <button
              type="button"
              className="rounded border border-rose-400 px-2 py-0.5 text-xs text-rose-700"
              onClick={() => write(removeLibraryExtra(tablePath, customization, e.id))}
            >
              {t("edit.delete")}
            </button>
          </li>
        ))}
      </ul>
      <form onSubmit={handleAdd} className="grid grid-cols-[1fr_auto_auto] gap-2 text-sm">
        <input
          className="rounded border px-2 py-1"
          placeholder={t("customization.extras.name-placeholder")}
          value={name}
          onChange={(e) => setName(e.target.value)}
          aria-label={t("customization.extras.new-name")}
        />
        <select
          className="rounded border px-2 py-1"
          value={kind}
          onChange={(e) => setKind(e.target.value as CustomExtra["kind"])}
          aria-label={t("customization.extras.new-kind")}
        >
          {KINDS.map((k) => (
            <option key={k} value={k}>
              {k}
            </option>
          ))}
        </select>
        <button type="submit" className="rounded border px-2 py-1">
          {t("edit.save")}
        </button>
      </form>
    </section>
  );
}
